import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ReloadTeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  useWalkthrough,
  useWalkthroughSignal,
} from "@redotech/redo-merchant-app-common/walkthrough/use-walkthrough";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { memo, useCallback, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TaskContextualModalRenderer } from "./components/contextual-modal/task-contextual-modal-renderer";
import { IntakeModal } from "./components/intake-modal/intake-modal";
import {
  SetupWelcomeGradient,
  SetupWelcomePanel,
} from "./components/welcome-overlay/setup-welcome-overlay";
import { SetupGuideWidget } from "./components/widget/setup-guide-widget";
import { WidgetResumeReminder } from "./components/widget/widget-resume-reminder";
import { computeHostSurfaces } from "./provider/compute-host-surfaces";
import { useSetupGuideOptional } from "./provider/use-setup-guide";
import {
  isWalkthroughSetupTaskCompletedPayload,
  WALKTHROUGH_SETUP_TASK_COMPLETED_SIGNAL,
} from "./provider/walkthrough-setup-task-signal";
import { getProductConfig } from "./registry/product-registry";
import type { IntakeAnswers, Product } from "./registry/types";
import { submitSetupGuideIntakeWithActivation } from "./setup-guide-host-actions";
import * as css from "./setup-guide-host.module.css";

/**
 * Mounts the floating setup-guide UI surfaces (intake modal, bottom-right
 * widget, contextual modal renderer) inside the SetupGuideProvider tree.
 *
 * Renders nothing when:
 *   - flag is off (provider returns null context)
 *   - no team loaded yet
 *   - no active product (legacy merchant or fresh-with-no-row)
 *   - widget state hides the panel for this device
 *
 * Each surface owns its own visibility logic; this component just
 * forwards props from the hook so the design-system components stay
 * test-friendly with simple props.
 */
export const SetupGuideHost = memo(function SetupGuideHost() {
  const setupGuide = useSetupGuideOptional();
  const team = useContext(TeamContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const reloadTeam = useRequiredContext(ReloadTeamContext);
  const location = useLocation();
  const navigate = useNavigate();
  const walkthrough = useWalkthrough();

  // In-session welcome overlay flag. Flipped true the moment intake
  // submit succeeds; flipped false on Got It. Intentionally NOT
  // persisted — the overlay is a one-shot transition celebration, so
  // a reload mid-confetti just dismisses it (the checklist still
  // shows). Avoids the localStorage / DB-column complexity of trying
  // to model "have you ever seen this?".
  const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false);

  const handleWalkthroughSetupTaskCompleted = useCallback(
    (payload: unknown) => {
      if (!setupGuide || !isWalkthroughSetupTaskCompletedPayload(payload)) {
        return;
      }
      if (setupGuide.activeProduct !== payload.product) {
        return;
      }
      void setupGuide
        .markTaskComplete(payload.product, payload.taskId)
        .catch(() => {});
    },
    [setupGuide],
  );
  useWalkthroughSignal(
    WALKTHROUGH_SETUP_TASK_COMPLETED_SIGNAL,
    handleWalkthroughSetupTaskCompleted,
  );

  const handleIntakeSubmit = useCallback(
    async (product: Product, answers: IntakeAnswers) => {
      if (!setupGuide) {
        return;
      }
      await submitSetupGuideIntakeWithActivation({
        product,
        answers,
        entrySource: setupGuide.activeProgress?.entrySource,
        activationSurface: setupGuide.activationSurface,
        setupGuide,
        rpcClient: merchantRpcClient,
        reloadTeam,
        onActivationComplete: () => setShowWelcomeOverlay(true),
      });
    },
    [setupGuide, merchantRpcClient, reloadTeam],
  );

  if (!setupGuide || !team) {
    return null;
  }
  const {
    activeProduct,
    activeProgress,
    asyncTaskStatuses,
    showIntake,
    widget,
    recordTaskVisited,
    signalTaskCompletion,
    dismissGuideForTeam,
  } = setupGuide;

  if (!activeProduct) {
    return null;
  }

  // Pure helper computes which floating surfaces should render. Modals
  // are scoped to the active product's routes so they don't follow
  // merchants into unrelated areas of the app.
  const activeConfig = getProductConfig(activeProduct);
  const {
    showIntakeModal,
    showResumeReminder,
    showWidget,
    showContextualModals,
  } = computeHostSurfaces({
    activeProduct,
    activeConfig,
    activeProgress,
    showIntake,
    widgetVisualState: widget.visualState,
    pathname: location.pathname,
    suppressForWalkthrough: walkthrough?.active ?? false,
  });

  // Welcome overlay only renders during the intake → dashboard
  // transition. Anchored visually to the widget so it must be
  // expanded too. Once dismissed via Got It in this session, never
  // returns until the merchant runs intake again.
  const showWelcomeOverlayNow =
    !walkthrough?.active &&
    showWelcomeOverlay &&
    !showIntake &&
    widget.visualState === "expanded";

  return (
    <>
      {showIntakeModal ? (
        <IntakeModal
          animateStepTransitions
          onClose={() => {
            void setupGuide.dismissGuideForTeam(activeProduct);
          }}
          onSubmit={(answers) => handleIntakeSubmit(activeProduct, answers)}
          product={activeProduct}
        />
      ) : null}

      {showWelcomeOverlayNow ? <SetupWelcomeGradient /> : null}

      {showWidget || showWelcomeOverlayNow || showResumeReminder ? (
        <div className={css.bottomRightDock}>
          {showWelcomeOverlayNow ? (
            <SetupWelcomePanel onDismiss={() => setShowWelcomeOverlay(false)} />
          ) : null}
          {showResumeReminder && activeConfig && team ? (
            <WidgetResumeReminder
              displayName={activeConfig.displayName}
              onClick={() => {
                const url = new URL(
                  activeConfig.resumeRoute(team._id.toString()),
                  window.location.origin,
                );
                url.searchParams.set("setupSource", "widget");
                navigate(`${url.pathname}${url.search}`);
              }}
            />
          ) : null}
          {showWidget && activeProgress ? (
            <SetupGuideWidget
              asyncTaskStatuses={asyncTaskStatuses}
              onClose={widget.closeForDevice}
              onCompletionDismiss={() => {
                void dismissGuideForTeam(activeProduct);
              }}
              onTaskClick={(taskId) => recordTaskVisited(activeProduct, taskId)}
              product={activeProduct}
              progress={activeProgress}
            />
          ) : null}
        </div>
      ) : null}

      {showContextualModals ? (
        <TaskContextualModalRenderer
          onTaskCompleted={(taskId) =>
            signalTaskCompletion({ product: activeProduct, taskId })
          }
          product={activeProduct}
        />
      ) : null}
    </>
  );
});
